import { EditorThemeClasses } from 'lexical';

/**
 * Lexical theme configuration for EdgeCalendar
 * Follows EdgeLex design system with legal-specific styling
 */
export const calendarLexicalTheme: EditorThemeClasses = {
  // Base editor styling
  root: 'calendar-editor-root relative min-h-[120px] p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus-within:ring-2 focus-within:ring-lex-accent focus-within:border-lex-accent',
  
  // Paragraph styling
  paragraph: 'calendar-editor-paragraph mb-2 text-gray-800 dark:text-gray-200 leading-relaxed',
  
  // Text formatting
  text: {
    bold: 'font-semibold text-gray-900 dark:text-gray-100',
    italic: 'italic',
    underline: 'underline',
    strikethrough: 'line-through',
    code: 'bg-gray-100 dark:bg-gray-700 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded text-sm font-mono',
  },

  // Headings
  heading: {
    h1: 'text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2',
    h2: 'text-xl font-semibold mb-3 text-gray-800 dark:text-gray-200',
    h3: 'text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200',
    h4: 'text-base font-semibold mb-2 text-gray-800 dark:text-gray-200',
    h5: 'text-sm font-semibold mb-1 text-gray-800 dark:text-gray-200',
    h6: 'text-xs font-semibold mb-1 text-gray-800 dark:text-gray-200',
  },

  // Lists
  list: {
    ul: 'list-disc list-inside mb-4 pl-4 space-y-1',
    ol: 'list-decimal list-inside mb-4 pl-4 space-y-1',
    listitem: 'text-gray-700 dark:text-gray-300',
    nested: {
      listitem: 'text-gray-600 dark:text-gray-400',
    },
  },

  // Links
  link: 'text-lex-accent underline hover:text-lex-accent-hover cursor-pointer',

  // Quote blocks
  quote: 'border-l-4 border-lex-accent pl-4 italic text-gray-600 dark:text-gray-400 bg-lex-accent-soft py-2 mb-4 rounded-r',

  // Code blocks (VS Code-style container)
  code: 'lex-code-block bg-[#1e1e1e] text-[#d4d4d4] p-4 rounded-lg mb-4 font-mono text-sm overflow-x-auto border border-gray-700 leading-relaxed',

  // Syntax highlighting tokens — VS Code Dark+ color palette
  codeHighlight: {
    atrule: 'text-[#c586c0]',
    attr: 'text-[#9cdcfe]',
    boolean: 'text-[#569cd6]',
    builtin: 'text-[#4ec9b0]',
    cdata: 'text-[#6a9955]',
    char: 'text-[#ce9178]',
    class: 'text-[#4ec9b0]',
    'class-name': 'text-[#4ec9b0]',
    comment: 'text-[#6a9955] italic',
    constant: 'text-[#4fc1ff]',
    deleted: 'text-[#ce9178]',
    doctype: 'text-[#6a9955]',
    entity: 'text-[#569cd6]',
    function: 'text-[#dcdcaa]',
    important: 'text-[#569cd6] font-bold',
    inserted: 'text-[#b5cea8]',
    keyword: 'text-[#569cd6]',
    namespace: 'text-[#4ec9b0]',
    number: 'text-[#b5cea8]',
    operator: 'text-[#d4d4d4]',
    prolog: 'text-[#6a9955]',
    property: 'text-[#9cdcfe]',
    punctuation: 'text-[#d4d4d4]',
    regex: 'text-[#d16969]',
    selector: 'text-[#d7ba7d]',
    string: 'text-[#ce9178]',
    symbol: 'text-[#b5cea8]',
    tag: 'text-[#569cd6]',
    url: 'text-[#3794ff] underline',
    variable: 'text-[#9cdcfe]',
  },

  // Horizontal rule
  horizontalRule: 'lex-hr border-t-2 border-gray-300 dark:border-gray-600 my-4',

  // Legal entity nodes (custom pills)
  legalEntity: {
    contact: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-lex-accent-soft text-lex-accent border border-lex-accent cursor-pointer hover:bg-lex-state-hover transition-colors',
    matter: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200 border border-green-200 dark:border-green-700 cursor-pointer hover:bg-green-200 dark:hover:bg-green-800/50 transition-colors',
    bill: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-700 cursor-pointer hover:bg-yellow-200 dark:hover:bg-yellow-800/50 transition-colors',
    document: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200 border border-purple-200 dark:border-purple-700 cursor-pointer hover:bg-purple-200 dark:hover:bg-purple-800/50 transition-colors',
  },

  // Event block styling (for Interactive Agenda)
  eventBlock: 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-4 shadow-sm',

  // Placeholder styling
  placeholder: 'calendar-editor-placeholder absolute top-3 left-3 text-gray-500 dark:text-gray-400 pointer-events-none select-none',

  // Interactive Agenda specific styling
  agendaEditor: {
    root: 'agenda-editor-root min-h-screen p-6 bg-white dark:bg-gray-900',
    paragraph: 'agenda-paragraph mb-3 text-gray-800 dark:text-gray-200 leading-relaxed',
    eventBlock: 'agenda-event-block bg-lex-accent-soft border-l-4 border-lex-accent pl-4 py-3 mb-6 rounded-r-lg shadow-sm',
    freeText: 'agenda-free-text text-gray-700 dark:text-gray-300 min-h-[2rem] focus:outline-none',
  },

  // Template editor styling
  templateEditor: {
    root: 'template-editor-root min-h-[300px] p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800',
    placeholder: 'template-placeholder text-gray-400 dark:text-gray-500 text-center py-8',
  },
};

/**
 * CSS class utilities for legal entity styling
 */
export const legalEntityClasses = {
  contact: 'legal-entity-contact',
  matter: 'legal-entity-matter', 
  bill: 'legal-entity-bill',
  document: 'legal-entity-document',
  eventBlock: 'legal-entity-event-block',
} as const;

/**
 * Trigger characters for entity references
 */
export const entityTriggers = {
  CONTACT: '@',
  MATTER: '#',
  BILL: '$',
  DOCUMENT: '/doc',
} as const; 